/** @module FormulatedProduct/_FormulatedProductSampleFormMixin */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi",
	"csi/_base/modules",
	"csi/_base/underscore",
	"csi/api",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/def",
	"csi/def/Action",
	"csi/def/Field",
	"csi/def/Relationship",
	"csi/def/ViewRef",
	"csi/form/Widget",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/reflection",
	"csi/sessionConstants",
	"csi/store/_base",
	"csi/store/Names",
	"csi/xml",
	"csi/xml/Query",
	"Apparel/model/productSource",
	"FormulatedProduct/model/foodSource",
	"Sourcing"
], function(declare, lang, csi, modules, _, api, comm, PostRequest, def, Action, Field, Relationship, ViewRef, Widget,
	html, locale, message, reflection, sessionConstants, csiStore, Names, xml, Query, {ATTR_SOURCE_SAMPLES, ATTR_SOURCING_SOURCES}, {TYPE_FOOD_SOURCE}, Sourcing) {

	/**
	 *  SamplesForm is used from many places:
	 *  	Product (Food, FormulatedMaterial) Samples view
	 *  	FoodSource and FormulatedMaterialSource Samples view
	 *  	SupplierRequest view of Samples
	 */

	var _SetupClass = declare(null, {
		runRequest: true,

		/**
		 * summary: add urls and paths needed for product
		 * @param {Widget} form
		 * @param {Controller} ac
		 * @param {Array} urls
		 * @param {Array} paths
		 */
		request: function(form, ac, urls, paths) {
			if (form.urlProduct) {
				urls.push(form.urlProduct);
			}
		},

		/**
		 * summary: initialize defaults based on Product
		 * @param {Widget} form
		 * @param {Controller} ac
		 */
		init: function(form, ac) {
			form.addDefaultValue(xml.ATTR_NAME, ac.m_formData.getFieldValue(xml.ATTR_NAME));

			form.addDefaultValue("ResponsibleUsers", [sessionConstants.userUrl]);

			return null;
		}
	});

	return declare(Widget, {
		'class': 'csi-form-view-block',

		// field ids
		LINE_ITEM: "SampleSRLineItem",
		QUANTITY: "RequestSampleQuantity",
		SAMPLE_STORAGE: "SampleStorage",
		SUPPLIER: "Supplier",
		FACTORY: "Factory",
		SUPPLIER_FACTORY: "SupplierFactory",
		CUSTOM_QUANTITY: "RequestSampleCustomQuantity",
		COPY_SAMPLE_BOM: "CopySampleBOM",


		/** @property {string} urlProduct - URL to Product for which samples are being created */
		urlProduct: null,

		/** @property {string} urlRequest - URL to SupplierRequest and optional line item for which samples are being created */
		urlRequest: null,

		/** @property {cnl} sourceCNL - CNL to Source for which samples are being created */
		sourceCNL: null,

		/** @property {string} sourceName - Name to Source for which samples are being created */
		sourceName: null,

		/** @property {array} _defaultBaseSize, _defaultSizes - list of all available sizes */
		_defaultBaseSize: null,
		_defaultSizes: null,

		// MatrixEditor: custom quantity matrix editor
		_matrix: null,

		// DOMNode: row containing custom quantity matrix
		_matrixRow: null,

		// String|Function: class to use to initialize form
		_productSetupClass: _SetupClass,

		// Object: instance of _productSetupClass
		_productSetup: null,

		// String: type of Sample to create based on product type
		_typeSample: null,

		// String: currently selected base size
		_urlBaseSize: null,

		// Int: keep track of how many Samples were created for batch purpose
		batchSampleCtr: null,

		/**
		 * @override
		 * If field is for LineItem or Supplier, then add SelectAll option
		 * @param {Field} field
		 * @param {String} value - initial value for the field
		 */
		getFieldStore: function(field, value) {
			var name = field.getControlName();

			if (name == this.LINE_ITEM && this.urlRequest) {
				return this._getLineItemFieldStore(value);
			}

			return this.inherited(arguments);
		},

		/**
		 * Handle retrieving data for lineitem field stores
		 * @param {String} value - initial value for the field
		 */
		_getLineItemFieldStore: function(value) {
			var gotoDialog = "SampleSRLineItem";
			var params = {
				m_id: "LineItems",
				m_url: this.urlRequest,
				m_query: new Query().RefAttr("SRLineItemProduct", xml.OP_NOT_EQUALS, csi.EMPTY_URL)
			};

			return this._getStore(gotoDialog, params, value);
		},

		/**
		 * Handle retrieving of store and its action
		 * @param {String} gotoDialog - id of gotoDialog
		 * @param {String} params - different field params
		 * @param {String} value - initial value for the field
		 */
		_getStore: function(gotoDialog, params, value) {
			if (params) {
				var rel = lang.mixin(new Relationship(), params);
				var storeOptions = csiStore.namesOptionsByRelationship(this.getContext(), rel);
				storeOptions.action = lang.mixin(new Action(), {
					m_name: locale.resource("AggregateInline_Name", [locale.typePlural(gotoDialog)]),
					m_viewRef: lang.mixin(new ViewRef(), params),
					m_fixedQuery: params.m_query // possible extra filter
				});

				// make sure current value(s) are in store so it does not have to be queried
				var store = new Names(storeOptions);
				var node;

				if (value != null && (node = this.cacheView.getNode(value, true))) {
					var extraItem = csiStore.nameObject(node);
					store.put(extraItem);
				}

				return store;
			}
		},

		/**
		 * @override
		 * set product for form indicating the type of sample to create
		 */
		setProduct: function(/*Node*/product, /*String|Function?*/setupClass) {
			if (!product) {
				return;
			}

			this.cacheView.cacheNode(product);
			this.urlProduct = product.$URL;
			this._setProductType(product.$Type);

			if (setupClass) {
				this._productSetupClass = setupClass;
			}
		},

		/** Set Source URL and Name for which Samples will be created */
		setSource(urlSource, sourceName) {
			this.urlSource = urlSource;
			this.sourceName = sourceName;
			this.defView.removeField(this.SUPPLIER);
		},

		/**
		 * Set SupplierRequest for which sample is being created
		 * @param {Node} sr - Supplier Request
		 */
		setSupplierRequest: function(sr) {
			var cache = this.cacheView;
			var lineItem, product;

			if (sr) {
				cache.cacheNode(sr);
				this.urlRequest = sr.$URL;
				this._setProductType(xml.enumGetText(sr.RequestType));

				// init Supplier field
				let fSource = this.defView.getField(this.SUPPLIER) || this.defView.getField(this.FACTORY);
				if (fSource) {
					fSource.m_bRequired = true;
					fSource.m_relationship = lang.mixin(new Relationship(), {
						m_url: this.urlRequest,
						m_id: this.isSupplierFactory() ? "SRSupplierFactories" : "SRSources/Child:Supplier",
						m_query: this.isSupplierFactory() ? null : new Query().Attr("AutoCreateProductSourceOnSR", xml.OP_EQUALS, true).SortByNode(xml.PROP_NAME) // only Suppliers with AutoCreateProductSourceOnSR is set to true
					});

				}

				var fProductOnSr = this.defView.getField(this.LINE_ITEM);
				if (this.urlProduct) {
					// if product already specified, then find line item in request that points to product;
					// assumption is that product is guaranteed to already be in request
					_.some(sr.LineItems, function(url) {
						lineItem = cache.getNode(url, true);

						if (lineItem.SRLineItemProduct == this.urlProduct) {
							this.addFixedValue(this.LINE_ITEM, url);
							return true;
						}

						return false;
					}, this);
				} else {
					// init Food or Formulated Material field to select line item - pre-select if only one
					fProductOnSr.m_bRequired = true;
					fProductOnSr.m_relationship = lang.mixin(new Relationship(), {
						m_url: this.urlRequest,
						m_id: "LineItems"
					});
				}
			} else {
				// reset members
				this.urlRequest = null;
				this.addFixedValue(this.LINE_ITEM, null);
			}
		},

		/**
		 * write samples for each product/supplier combination - if for SupplierRequest, then also make
		 * sure to connect to SRLineItem
		 */
		toWriterSamples: function(ac) {
			var fd = this.defView.m_formData;
			var urlLineItem = fd.getFieldValue(this.LINE_ITEM, true);
			const fieldValue = fd.getFieldValue(this.SUPPLIER, true) || fd.getFieldValue(this.FACTORY, true);
			var sourceCNL = fieldValue || this.sourceCNL;
			var samples = [];
			this.batchSampleCtr = 0;

			let sr = this.cacheView.getNode(this.urlRequest);
			if (sr || (sourceCNL?.length > 0)) {
				var sources = xml.isValidURL(sourceCNL) ? [sourceCNL] : sr.SRsources;

				if (sources?.length > 0 && (sources[0] instanceof Array)) {
					sources = sources[0];
				}

				if (urlLineItem != null) {
					_.each(urlLineItem, function(l) {
						var lineItem = this.cacheView.getNode(l, true);
						var lineItemProduct = this.cacheView.getNode(lineItem.SRLineItemProduct, true);

						if (sr.RequestType == "DataPackageContainerType:Style" || !lineItemProduct.Hidden) {
							_.each(sources, function(s) {
								samples = samples.concat(this._toWriterProductSamples(ac, lineItem.SRLineItemProduct, s, lineItem.$URL));
							}, this);
						}
					}, this);
				} else {
					_.each(sources, function(s) {
						samples = samples.concat(this._toWriterProductSamples(ac, this.urlProduct, s));
					}, this);
				}
			} else {
				// create samples for single product and sources
				samples = samples.concat(this._toWriterProductSamples(ac, this.urlProduct, sourceCNL));
			}

			return samples;
		},

		_buildForm: function() {
			this.inherited(arguments);
			this._updateStatistics();
		},

		/**
		 * @override
		 * call setup instance to update form fields and defaults based on context
		 */
		_buildFields: function(fields) {
			this._preProcessProduct();

			this.inherited(arguments);

			this.setFieldHidden(this.LINE_ITEM, !this.urlRequest);

		},

		/**
		 * @override
		 * return relationship for field
		 * @param{Field} f
		 * @param{String} urlNode
		 * @param{String?} value
		 */
		_getFieldRelationship: function(f, urlNode, value) {
			return this.inherited(arguments);
		},

		/**
		 * get root request for nodes needed by view - forward call to setup instance
		 * as the request will differ depending on context of form
		 *
		 * @param{Node} node
		 */
		_getRootRequest: function(node) {
			var req = this.inherited(arguments);
			var ps = this._getProductSetup();
			var urls = [];
			var paths = [];

			if (ps && ps.runRequest) {
				ps.request(this, this.defView.getSource(), urls, paths);
			}

			if (this.urlRoot) {
				urls.push(this.urlRoot);
				paths.push("Child:SampleDataSheets");
			}

			if (urls.length) {
				req = req || new PostRequest(comm.MOD_SEARCH, comm.OP_URL);
				req.addParameter(comm.QUERY_URL, urls, true);
				req.addDependencyPaths(paths);
			}

			return req;
		},

		onChangeField(field) {
			this.inherited(arguments);
			this._updateStatistics();
		},

		_removeMatrix: function() {
			html.destroyElement(this._matrixRow);
			this._matrixRow = null;
			this._matrix = null;
			this._updateStatistics();
		},

		/**
		 * update dimensions based on selected product if line item field is visible
		 */
		_onChangeLineItem: function(defView, field, value) {
			if (!this.isFieldVisible(field)) {
				return;
			}

			var lineItem = (value.length == 1) ? this.cacheView.getNode(value, true) : null;
			var urlProduct = lineItem ? lineItem.SRLineItemProduct : null;

			if (urlProduct != this.urlProduct) {
				this._removeMatrix();
				this._resetProductData();
				this.urlProduct = urlProduct;

				if (urlProduct) {
					message.progress(locale.resource("Action_Message"));

					var req = new PostRequest(comm.MOD_SEARCH, comm.OP_URL);
					var ps = this._getProductSetup();
					var urls = [];
					var paths = [];
					ps.request(this, this.defView.getSource(), urls, paths);
					req.addParameter(comm.QUERY_URL, urls, true);
					req.addDependencyPaths(paths);
					req.go({
						load: lang.hitch(this, this._onLoadProduct)
					});
				} else {
					this._onLoadProduct();
				}
			}
		},

		/**
		 * re-build fields if product selected
		 * @param{Response?} res
		 */
		_onLoadProduct: function(res) {
			this.cacheView.cacheResponse(res);

			this.setProduct(this.cacheView.getNode(this.urlProduct));


			this.publishDomChange();
			message.hide(true);
		},

		/**
		 * Validate that custom quantities are within range
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {Object} params
		 */
		_onValidateCustomQuantity: function(defView, field, value, params) {
			if (this._matrix && !this._matrix.isValid()) {
				throw new Error("SamplesFormCustomQuantityInvalid");
			}
		},

		_preProcessProduct: function() {
			// summary: return array of possible dimension values for given product
			var ps = this._getProductSetup();
			if (!ps) {
				this._setProductDimensions([], [], null, true);
			}
			return ps ? ps.init(this, this.defView.getSource()) : null;
		},

		_redraw: function() {
			// summary: reset product if there is supplier request and product field is not fixed
			if (this.urlProduct && this.urlRequest && !this.getFixedValue(this.LINE_ITEM)) {
				this._resetProductData();
			}

			this.inherited(arguments);
		},

		_resetProductData: function() {
			// summary: reset product members back to defaults
			this._defaultBaseSize = null;
			this.urlProduct = null;
			this.matrixRow = null;
		},

		/**
		 * @override
		 * make SizeChart evaluate form work (in that case, form is in wizard so need different way to access source controller),
		 * but this doesn't seem to do the right thing in case of ProductSource samples; urlProduct references the ProductSource rather than Product
		 */
		_resolveView: function() {
			var source = this.isNew() ? null : this.getSourceController();
			const cache = this.getCache();
			let fd = this.defView.m_formData;
			const parentNode = fd.getSource().getParent();

			if (source) {
				this.urlProduct = source.m_urlParent;
			}

			if (!this.getViewNode()) {
				// add Source field only if Sourcing is installed - otherwise new samples will go to a default source
				if (modules.hasModule("Sourcing")) {
					this.insertField(lang.mixin(new Field(), {
						bForceEditable: true,
						m_id: this.LINE_ITEM,
						m_typeControl: def.FIELD_MULTI_SELECT,
						widgetParams: {defaultAll: true},
						m_onChange: "_onChangeLineItem"
					}), 0);

					this.insertField(lang.mixin(new Field(), {
						m_bRequired: true,
						m_label: this.isSupplierFactory() ? this.FACTORY : this.SUPPLIER,
						m_id: "ProductSources",
						m_path: xml.path(xml.ATTR_PARENT, xml.ATTR_PARENT),
						// prevent the form from sending a request of changing productSources to the function queryAggregateStore
						// Setting omitFromReport to false will return null from queryAggregateStore, which breaks the form
						omitFromReport: true,
						bForceEditable: true,
					}), 0);

					//set supplier / supplier factory dropdown to None by default
					const productSourcing = cache.getNode(parentNode.ProductSourcing) || cache.getNode(parentNode.__Parent__);
					const designatedProductSource = cache.getNode(productSourcing?.DesignatedProductSource);
					this.defView.m_formData.setFieldInitialValue(this.SUPPLIER, designatedProductSource ? [designatedProductSource.Supplier] : []);
					this.defView.m_formData.setFieldInitialValue(this.FACTORY, designatedProductSource ? [designatedProductSource.SupplierFactory] : []);

					this.insertField(lang.mixin(new Field(), {
						m_label: this.QUANTITY,
						m_type: xml.ATTR_INTEGER,
						widgetParams: {constraints: {min: 1, max: api.getSamplesQuantityMax()}}
					}));
				}
			}

			this.addDefaultValue(this.QUANTITY, 1);

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Attach form node to parent product
		 */
		onWriteFormReport(field, writer, formInfo) {
			const {nodeUrl, parentUrl} = formInfo;

			// if creating Sample directly under FoodSource, then nothing else to do
			if (parentUrl) {
				return;
			}

			// if creating under product, then create path up to product
			const {cacheView, urlProduct} = this;
			const product = cacheView.get(urlProduct);
			if (product) {
				writer.createNode(null, TYPE_FOOD_SOURCE)
					.attach(product.ProductSourcing, ATTR_SOURCING_SOURCES)
					.changeAttribute(ATTR_SOURCE_SAMPLES, xml.ATTR_REFLIST, [nodeUrl]);
			}
		},
		

		_buildView: function() {
			this.inherited(arguments);
		},

		_setProductDimensions: function(/*String[]*/colors, /*String[]*/sizes, /*String?*/urlBS, /*Boolean*/bNone) {
			this._urlBaseSize = xml.isValidURL(urlBS) ? urlBS : null;
		},

		/**
		 * Checks if FDD is enabled - if Product Source Creation Level is Factory 
		 */
		isSupplierFactory() {
		    let fd = this.defView.m_formData;
			const cache = this.getCache();
	
			const productSourceCreationLevel = Sourcing.getProductSourceCreationLevel(fd.m_source.m_urlParent , cache); 
			return productSourceCreationLevel == "ProductSourceCreationLevel:Factory";
		},

		/**
		 * Generates Sample prefix name
		 */
		generateSampleName(urlSource){
			const fd = this.defView.m_formData;
			var field = fd.getField(this.SUPPLIER) || fd.getField(this.FACTORY);
			var sourceName = field ? field.getNameForValue(urlSource) : this.sourceName;
			const cacheView = this.cacheView;
			if(fd.getField(this.FACTORY) != null){
				const sourceNode = cacheView.getNode(urlSource);
				const factoryURL = sourceNode.Factory;
				const factoryNode = cacheView.getNode(factoryURL);
				sourceName = sourceName || factoryNode.$Name;
			}
			const prefix = fd.getFieldValue(xml.ATTR_NAME, true) == "" ? sourceName : fd.getFieldValue(xml.ATTR_NAME, true) + " - " + sourceName;

			return prefix
		}
	});
});